<template lang="pug">
	.easy-creative--script-table.script-items.full-width(:class="{ 'active': isScriptItemsHighlighted }")
		table.flex-1(border="0" cellspacing="0" cellpadding="0")
			tr.script-item(v-for="(item, idx) in visibleScriptItems", :key="item.id", :style="{ '--bg': getBg(item) }", :idx="idx", :class="[isNotText(item) ? 'not-text' : '', `type-${item.type}`, itemClass]")
				td.nowrap.role-td
					.flex-col.align-end.role
						.flex-col.align-end.voice-script-elem(v-if="item.actualVoice || item.type === 'text'")
							.upper.flex-row.space.align-center
								fas(v-if="getItemIcon(item)", :icon="getItemIcon(item)", :style="{ color: getItemIconColor(item) }")
								.voice-image-holder(v-if="item.actualVoice && item.actualVoice.voice.ImageUrl")
									.voice-image.hoverable.clickable(:id="`voice-image-${idx}`", :style="{ '--image-url': `url('${item.actualVoice.voice.ImageUrl}')` }", 
										@click="toggleVoiceInfo && toggleVoiceInfo(item.actualVoice, idx)", @mouseover="toggleVoiceInfo && toggleVoiceInfo(item.actualVoice, idx, true)")
								span(:title="getVoiceDescription(item)") {{getVoiceDescription(item, true)}}
							.adjectives.flex-row.grey(v-if="item.voiceAdjectives && item.voiceAdjectives.length") {{item.voiceAdjectives.join(', ')}}
						.flex-row.space.align-center(v-else)
							.flex-row.align-center.space(:class="{ 'editable-item-title': allowItemDisabling(item) }", @click="onClickEditableItemTitle(item, idx)")
								fas.grey(v-if="getItemIcon(item)", :icon="getItemIcon(item)")
								TextVisualizer.upper(v-if="preventEdit", :text="item.description || item.type", :textKey="item.id")
								.upper(v-else) {{item.description || item.type}}
								.grey(v-if="item.length") ({{item.length}} {{$tCaption('sec.')}})
							Switcher(v-if="item.__isEditing && allowItemDisabling(item)", :value="!item.disabled", @change="v => $set(item, 'disabled', !v)")
				td.text-td(:class="{ 'disabled-controls': !!item.disabled }")
					.flex-col.space
						TextVisualizer.font-lg(v-if="preventEdit && item.value", :text="item.value", :textKey="item.id")
						EditableLabel.flex-1(v-else-if="item.value", :ref="`label_${item.id}`", @editing="v => $set(item, '__isEditing', v)")
							.font-lg {{item.value}}
							.flex-col.space(slot="editor")
								template(v-if="item.type === 'MUSIC'")
									TagEditor(:value="item.value", @change="v => item.value = v")
								template(v-else)
									DxTextArea.script-text-area(slot="editor", :value.sync="item.value", @value-changed="$refs[`label_${item.id}`][0].release()", stylingMode="filled")
						.phonemes.flex-row.wrap.space(v-if="item.phonemes && item.__isEditing")
							.phoneme.flex-row.space-small.align-center(v-for="(ph, phIdx) in item.phonemes", :title="$tCaption('[WORD] will be pronounced like [PHONEME]').replace(/\\[WORD\\]/g, ph.word).replace(/\\[PHONEME\\]/g, ph.phoneme)")
								.word {{ph.word}}:
								i.ph {{ph.phoneme}}
								.adt-button.rounded.padded.flex-row.align-center.center.clickable.hoverable(@click="item.phonemes.splice(phIdx, 1)")
									fas(icon="times")
					.actions-holder.flex-row.align-center.center(v-if="item.__isEditing")
						slot(name="actions", :item="item")
</template>

<script>
	import Loader from 'app-core/Loader.vue';
	import Switcher from 'app-core/Switcher.vue';
	import EditableLabel from 'app-core/EditableLabel.vue';
	import DxTextArea from 'devextreme-vue/text-area';
	import TagEditor from './EasyCreativeTagEditor.vue';
	import TextVisualizer from 'app-core/TextVisualizer.vue';

	export default {
		translationCategory: 'creative.EasyCreative',
		name: 'easy-creative-script-table',
		props: ['info', 'script', 'isScriptItemsHighlighted', 'toggleVoiceInfo', 'preventEdit', 'itemClass'],
		components: {
			Loader, Switcher, EditableLabel, DxTextArea, TagEditor, TextVisualizer
		},
		data() {
			return {
				
			};
		},
		computed: {
			isArray() {
				return Array.isArray(this.script);
			},
			isText() {
				return !this.isArray;
			},
			music() {
				return this.isArray && this.script.find(f => `${f.type}`.toLowerCase() === 'music') || null;
			},
			duration() {
				return this.isArray && this.script.find(f => `${f.type}`.toLowerCase() === 'duration') || null;
			},
			visibleScriptItems() {
				return this.isArray && this.script?.filter(f => !f.hide) || [];
			},
		},
		watch: {
		
		},
		mounted() {
			if (__DEBUG__)
				window.easyCreativeScriptTable = this;
		},
		destroyed() {
		},
		methods: {
			isNotText(item) {
				return item?.type?.toLowerCase() !== 'text';
			},
			getBg(item) {
				const itemKey = JSON.stringify({...item, value: null});
				if (this.$lastRenderedItem !== itemKey) {
					this.$lastRenderedItem = itemKey;
					this.$lastColor = this.$lastColor === '#fff' ? '#f8f8f8' : '#fff';
				}
				return this.$lastColor;
			},
			getItemIcon(item) {
				switch(`${item.type}`.toLowerCase()) {
					case 'text':
						return item.voice_gender === 'male' ? 'mars' : (item.voice_gender === 'female' ? 'venus' : 'microphone');
					case 'music':
						return 'music';
					case 'sfx':
					case 'ambience':
						return 'volume-high';
					case 'processing-template':
						return 'wand-magic-sparkles';
					case 'genre-adjectives':
						return 'sliders';
				}
			},
			getItemIconColor(item) {
				switch(`${item.type}`.toLowerCase()) {
					case 'text':
						return item.voice_gender === 'male' ? '#7795c5' : (item.voice_gender === 'female' ? '#c577c3' : 'var(--branding-color)');
				}
			},
			onClickEditableItemTitle(item, idx) {
				if (this.allowItemDisabling(item)) {
					this.$set(item, '__isEditing', !item.__isEditing);
					const label = this.$refs[`label_${idx}`] && this.$refs[`label_${idx}`][0];
					if (label) {
						label.isEditing = true;
					}
				}
			},
			allowItemDisabling(item) {
				return !this.preventEdit && ['music', 'sfx', 'ambience'].includes(`${item.type}`.toLowerCase());
			},
			getVoiceDescription(item, short) {
				return `${item.actualVoice ? `${item.actualVoice.displayName}`.split(' ')[0] : `${item.voice_gender} ${item.voice_number}`} (${short ? item.shortAdjective : this.$Data.capitalize(item.voice)}):`
			},
		}
	};
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	.easy-creative--script-table {
		
	}
</style>
